import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { Header } from "@/pages/CollegePredictorPage";
import {
  AreaChart, Area, XAxis, YAxis, ResponsiveContainer, ReferenceLine, Tooltip,
} from "recharts";
import { TrendingUp, Loader2 } from "lucide-react";

const EXAMS = {
  "JEE Main": 300, "JEE Advanced": 360, "NEET": 720, "CUET": 800,
};

export default function RankPredictorPage() {
  const [params] = useSearchParams();
  const [exam, setExam] = useState(EXAMS[params.get("exam")] ? params.get("exam") : "JEE Main");
  const [score, setScore] = useState(params.get("score") != null && !isNaN(Number(params.get("score"))) ? Number(params.get("score")) : 180);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const predict = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/predict/rank", { exam, score: Number(score) });
      setResult(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Prediction failed");
    } finally {
      setLoading(false);
    }
  };

  // bell curve visualization
  const curve = Array.from({ length: 41 }, (_, i) => {
    const x = (i / 40) * EXAMS[exam];
    const mean = EXAMS[exam] * 0.42, sd = EXAMS[exam] * 0.16;
    const y = Math.exp(-0.5 * Math.pow((x - mean) / sd, 2));
    return { score: Math.round(x), density: Number(y.toFixed(3)) };
  });

  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-8 py-8">
      <Header icon={TrendingUp} eyebrow="AI Rank Predictor"
        title="Predict your rank from your score" desc="Built on a decade of JEE, NEET & CUET result trends." />

      <div className="mb-6 rounded-xl border border-amber-400/20 bg-amber-400/5 px-4 py-2.5 text-xs text-amber-300" data-testid="prediction-disclaimer">
        Predicted ranks are statistical estimates, not guaranteed results. Actual ranks depend on the final answer key & normalization.
      </div>

      <div className="rounded-2xl border border-white/8 bg-[#131C2E] p-6">
        <div className="grid sm:grid-cols-3 gap-5 items-end">
          <div className="sm:col-span-2">
            <label className="text-sm font-medium text-slate-300">Exam</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {Object.keys(EXAMS).map((e) => (
                <button key={e} onClick={() => { setExam(e); setResult(null); }}
                  className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-all ${
                    exam === e ? "bg-cyan-400 text-[#041016]" : "bg-white/5 text-slate-400 hover:text-white"
                  }`}>{e}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-300">Your Score (max {EXAMS[exam]})</label>
            <input data-testid="rank-score-input" type="number" max={EXAMS[exam]} value={score}
              onChange={(e) => setScore(e.target.value)}
              className="mt-2 w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2.5 text-sm text-white outline-none focus:border-cyan-400/50 font-mono-data" />
          </div>
        </div>
        <input type="range" min={0} max={EXAMS[exam]} value={score} onChange={(e) => setScore(e.target.value)}
          className="mt-5 w-full accent-cyan-400" />
        <button data-testid="rank-predict-btn" onClick={predict} disabled={loading}
          className="mt-5 inline-flex items-center gap-2 rounded-full bg-cyan-400 px-7 py-3 text-sm font-bold text-[#041016] transition-transform hover:scale-105 disabled:opacity-50">
          {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Predicting...</> : <><TrendingUp className="h-4 w-4" /> Predict Rank</>}
        </button>
      </div>

      {result && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-8 grid sm:grid-cols-3 gap-4">
          <div data-testid="rank-result-badge" className="rounded-2xl border border-cyan-400/30 bg-cyan-400/5 p-6 text-center">
            <p className="text-xs uppercase tracking-wide text-slate-400">Predicted Rank</p>
            <p className="mt-2 font-mono-data text-3xl font-extrabold text-cyan-300">{result.predicted_rank.toLocaleString()}</p>
            <p className="mt-1 text-xs text-slate-500">Range {result.rank_range[0].toLocaleString()}–{result.rank_range[1].toLocaleString()}</p>
          </div>
          <div className="rounded-2xl border border-white/8 bg-[#131C2E] p-6 text-center">
            <p className="text-xs uppercase tracking-wide text-slate-400">Percentile</p>
            <p className="mt-2 font-mono-data text-3xl font-extrabold text-emerald-300">{result.percentile}</p>
          </div>
          <div className="rounded-2xl border border-white/8 bg-[#131C2E] p-6 text-center">
            <p className="text-xs uppercase tracking-wide text-slate-400">Confidence</p>
            <p className="mt-2 font-mono-data text-3xl font-extrabold text-amber-300">{result.confidence}%</p>
          </div>
        </motion.div>
      )}

      <div className="mt-6 rounded-2xl border border-white/8 bg-[#131C2E] p-6">
        <h3 className="font-display text-lg font-semibold mb-4">Score distribution</h3>
        <div style={{ width: "100%", height: 240 }}>
          <ResponsiveContainer>
            <AreaChart data={curve}>
              <defs>
                <linearGradient id="bell" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#06B6D4" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#06B6D4" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="score" tick={{ fill: "#64748b", fontSize: 11 }} />
              <YAxis hide />
              <Tooltip contentStyle={{ background: "#0F1626", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12 }} />
              <Area type="monotone" dataKey="density" stroke="#06B6D4" fill="url(#bell)" strokeWidth={2} />
              {result && <ReferenceLine x={curve.reduce((p, c) => Math.abs(c.score - result.score) < Math.abs(p.score - result.score) ? c : p).score} stroke="#F59E0B" strokeWidth={2} strokeDasharray="4 4" label={{ value: "You", fill: "#F59E0B", fontSize: 12 }} />}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
